import React, { useEffect, useState, useMemo } from "react";
import {
  Flex,
  Box,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  IconButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  HStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useToast,
  useColorModeValue,
  Spinner,
} from "@chakra-ui/react";
import { AddIcon, EditIcon, DeleteIcon, SearchIcon, ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardHeader from "components/Card/CardHeader.js";
import {
  getPegawai,
  addPegawai,
  updatePegawai,
  deletePegawai,
} from "services/master/pegawaiService";

export default function MasterPegawai() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [editing, setEditing] = useState(false);
  const toast = useToast();
  const textColor = useColorModeValue("gray.700", "white");

  const initialForm = {
    NIP: "",
    NAMA: "",
    JABATAN: "",
    UNIT: "",
    KD_UNIT: "",
    EMAIL: "",
    NOREG: "",
  };
  const [form, setForm] = useState(initialForm);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await getPegawai();
      setData(res.data || []);
    } catch (err) {
      toast({
        title: "Gagal memuat data",
        description: err.response?.data?.message || err.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Filtered by Search
  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return data;
    const lower = searchTerm.toLowerCase();
    return data.filter(
      (item) =>
        (item.NAMA && item.NAMA.toLowerCase().includes(lower)) ||
        (item.NIP && item.NIP.toLowerCase().includes(lower)) ||
        (item.UNIT && item.UNIT.toLowerCase().includes(lower))
    );
  }, [data, searchTerm]);

  // Pagination calculation
  const totalPages = Math.max(1, Math.ceil(filteredData.length / pageSize));
  const currentPagedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPage, pageSize]);

  // Reset to page 1 on search or page size change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, pageSize]);

  const openAdd = () => {
    setForm(initialForm);
    setEditing(false);
    onOpen();
  };

  const openEdit = (row) => {
    setForm({
      NIP: row.NIP || "",
      NAMA: row.NAMA || "",
      JABATAN: row.JABATAN || "",
      UNIT: row.UNIT || "",
      KD_UNIT: row.KD_UNIT || "",
      EMAIL: row.EMAIL || "",
      NOREG: row.NOREG || "",
    });
    setEditing(true);
    onOpen();
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await updatePegawai(form.NIP, form);
        toast({
          title: "Berhasil diubah",
          status: "success",
          duration: 2000,
          isClosable: true,
        });
      } else {
        await addPegawai(form);
        toast({
          title: "Berhasil ditambah",
          status: "success",
          duration: 2000,
          isClosable: true,
        });
      }
      onClose();
      fetchData();
    } catch (err) {
      toast({
        title: "Gagal menyimpan",
        description: err.response?.data?.message || err.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDelete = async (nip) => {
    if (!window.confirm(`Yakin ingin menghapus pegawai dengan NIP: ${nip}?`)) return;
    try {
      await deletePegawai(nip);
      toast({
        title: "Berhasil dihapus",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      fetchData();
    } catch (err) {
      toast({
        title: "Gagal menghapus",
        description: err.response?.data?.message || err.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px" }}>
      <Card overflowX={{ sm: "scroll", xl: "hidden" }}>
        <CardHeader p="6px 0px 22px 0px" display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={3}>
          <Text fontSize="xl" color={textColor} fontWeight="bold">
            Master Pegawai
          </Text>
          <HStack spacing={3}>
            <InputGroup size="sm" w={{ base: "180px", md: "260px" }}>
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Cari nama / NIP..."
                borderRadius="15px"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
            <Button leftIcon={<AddIcon />} colorScheme="teal" size="sm" borderRadius="10px" onClick={openAdd}>
              Tambah Pegawai
            </Button>
          </HStack>
        </CardHeader>
        <CardBody>
          {loading ? (
            <Flex justify="center" p={8}>
              <Spinner size="xl" color="teal.500" />
            </Flex>
          ) : (
            <>
              <Table variant="simple" color={textColor}>
                <Thead>
                  <Tr my=".8rem" color="gray.400">
                    <Th color="gray.400">NIP</Th>
                    <Th color="gray.400">Nama</Th>
                    <Th color="gray.400">Jabatan</Th>
                    <Th color="gray.400">Unit</Th>
                    <Th color="gray.400">Kode Unit</Th>
                    <Th color="gray.400">Email</Th>
                    <Th color="gray.400">No Reg</Th>
                    <Th color="gray.400">Aksi</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {currentPagedData.length === 0 ? (
                    <Tr>
                      <Td colSpan={8} textAlign="center" py={6}>
                        {searchTerm ? "Data tidak ditemukan sesuai pencarian" : "Tidak ada data"}
                      </Td>
                    </Tr>
                  ) : (
                    currentPagedData.map((row) => (
                      <Tr key={row.NIP}>
                        <Td>{row.NIP}</Td>
                        <Td fontWeight="bold">{row.NAMA}</Td>
                        <Td>{row.JABATAN || "-"}</Td>
                        <Td>{row.UNIT || "-"}</Td>
                        <Td>{row.KD_UNIT || "-"}</Td>
                        <Td>{row.EMAIL || "-"}</Td>
                        <Td>{row.NOREG || "-"}</Td>
                        <Td>
                          <IconButton
                            aria-label="edit"
                            icon={<EditIcon />}
                            size="sm"
                            mr={2}
                            colorScheme="blue"
                            variant="ghost"
                            onClick={() => openEdit(row)}
                          />
                          <IconButton
                            aria-label="delete"
                            icon={<DeleteIcon />}
                            size="sm"
                            colorScheme="red"
                            variant="ghost"
                            onClick={() => handleDelete(row.NIP)}
                          />
                        </Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>

              {/* Pagination Controls */}
              <Flex justify="space-between" align="center" mt={4} flexWrap="wrap" gap={3}>
                <HStack spacing={2} align="center">
                  <Text fontSize="sm" color="gray.500">
                    Tampilkan
                  </Text>
                  <Select
                    size="sm"
                    w="80px"
                    borderRadius="8px"
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                  >
                    <option value={10}>10</option>
                    <option value={30}>30</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </Select>
                  <Text fontSize="sm" color="gray.500">
                    dari {filteredData.length} data
                  </Text>
                </HStack>

                <HStack spacing={2}>
                  <IconButton
                    aria-label="prev"
                    icon={<ChevronLeftIcon />}
                    size="sm"
                    isDisabled={currentPage <= 1}
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  />
                  <Text fontSize="sm">
                    {currentPage} / {totalPages}
                  </Text>
                  <IconButton
                    aria-label="next"
                    icon={<ChevronRightIcon />}
                    size="sm"
                    isDisabled={currentPage >= totalPages}
                    onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                  />
                </HStack>
              </Flex>
            </>
          )}
        </CardBody>
      </Card>

      {/* Modal Add/Edit */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editing ? "Ubah Pegawai" : "Tambah Pegawai"}</ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleSubmit}>
            <ModalBody>
              <FormControl isRequired mb={3}>
                <FormLabel>NIP</FormLabel>
                <Input
                  name="NIP"
                  value={form.NIP}
                  onChange={handleChange}
                  isDisabled={editing}
                  placeholder="Masukkan NIP"
                />
              </FormControl>
              <FormControl isRequired mb={3}>
                <FormLabel>Nama</FormLabel>
                <Input
                  name="NAMA"
                  value={form.NAMA}
                  onChange={handleChange}
                  placeholder="Masukkan Nama"
                />
              </FormControl>
              <FormControl mb={3}>
                <FormLabel>Jabatan</FormLabel>
                <Input
                  name="JABATAN"
                  value={form.JABATAN}
                  onChange={handleChange}
                  placeholder="Masukkan Jabatan"
                />
              </FormControl>
              <FormControl mb={3}>
                <FormLabel>Unit</FormLabel>
                <Input
                  name="UNIT"
                  value={form.UNIT}
                  onChange={handleChange}
                  placeholder="Masukkan Unit"
                />
              </FormControl>
              <FormControl mb={3}>
                <FormLabel>Kode Unit</FormLabel>
                <Input
                  name="KD_UNIT"
                  value={form.KD_UNIT}
                  onChange={handleChange}
                  placeholder="Masukkan Kode Unit"
                />
              </FormControl>
              <FormControl mb={3}>
                <FormLabel>Email</FormLabel>
                <Input
                  type="email"
                  name="EMAIL"
                  value={form.EMAIL}
                  onChange={handleChange}
                  placeholder="Masukkan Email"
                />
              </FormControl>
              <FormControl mb={3}>
                <FormLabel>No Registrasi</FormLabel>
                <Input
                  name="NOREG"
                  value={form.NOREG}
                  onChange={handleChange}
                  placeholder="Masukkan Nomor Registrasi"
                />
              </FormControl>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose}>
                Batal
              </Button>
              <Button type="submit" colorScheme="teal">
                Simpan
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </Flex>
  );
}
